import React, { useEffect, useContext } from 'react'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Grid, Stack } from '@mui/material'
import PackingListGrid from 'components/generic/datagrid/PackingListGrid'
import InventorySummaryBar from 'components/layouts/InventorySummaryBar'
import { useGetPackingListsQuery, useGetInventoryPackingListMetaQuery } from 'services/packingList'
import store from 'state/store'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import { COMMON_STATUSES, MODAL_VIEW_MODES } from 'utils/constants'
import { formatNumber, parseNumberObject } from 'utils/helpers'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'

const PackingListsList = () => {
  const { data, isLoading: isListLoading } = useGetPackingListsQuery({
    filter: JSON.stringify({
      statuses: [COMMON_STATUSES.AVAILABLE.id],
    }),
  })
  const { data: metaData, isLoading: isMetaDataLoading } = useGetInventoryPackingListMetaQuery({})
  const isLoading = isListLoading || isMetaDataLoading
  const { id } = useParams()

  const context = useContext(ContentLayoutContext)

  const { defaultYardUm } = useSelector(state => state.settings)
  const { defaultYardTimezone } = useSelector(state => state.auth)

  useEffect(() => {
    context.setContainerTitle('Packing Lists')
  }, [])

  useEffect(() => {
    if (id) {
      if (id === 'new') {
        store.dispatch(
          showFormModal({
            formModalName: FORM_MODALS.NEW_PACKING_LIST,
            viewMode: MODAL_VIEW_MODES.NEW,
          }),
        )
      } else {
        store.dispatch(showFormModal({ formModalName: FORM_MODALS.NEW_PACKING_LIST }))
      }
    }
  }, [id])

  const transformedRows = (data?.Items || [])
    .filter(packingList => packingList.status !== COMMON_STATUSES.VOID.id)
    .slice()
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
    .map(packingList => ({
      ...packingList,
      miniId: packingList?.tag,
      createdDate: packingList?.createdDate
        ? dateTimeFormatter(packingList?.createdDate, { timeZone: defaultYardTimezone })
        : '',
      materials: [...new Set(packingList?.finishedGoods.map(finishedGood => finishedGood.materialType.commonName))],
      netWeight: packingList.net ? `${packingList.net} ${defaultYardUm}` : `0 ${defaultYardUm}`,
      status: packingList?.status,
    }))

  const totalWeight = transformedRows.reduce((acc, next) => acc + Number(next?.net ? next?.net : 0), 0)

  const summaryItems = [
    {
      label: 'PACKING LISTS (AVAILABLE)',
      value: metaData?.packingListsAvailable?.weight?.amount
        ? parseNumberObject(metaData?.packingListsAvailable?.weight, { format: 'weight' })
        : '',
    },
    {
      label: 'TOTAL WEIGHT',
      value: formatNumber(`${totalWeight} ${defaultYardUm}`),
    },
    {
      label: 'LARGEST MATERIAL BY WEIGHT',
      value: metaData?.largestFinishedGoodMaterialByWeight,
    },
    {
      label: 'CREATED TODAY',
      value: metaData?.createdToday,
    },
    {
      label: 'CREATED YESTERDAY',
      value: metaData?.createdYesterday,
    },
  ]

  return (
    <Stack>
      <InventorySummaryBar items={summaryItems} />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <PackingListGrid rows={transformedRows} loading={isLoading} />
      </Grid>
    </Stack>
  )
}

export default PackingListsList
